import type { CachedBundle } from './types'

export interface ContentCache {
  get(key: string): Promise<CachedBundle | null>
  put(record: CachedBundle): Promise<void>
  delete(key: string): Promise<void>
  clear(): Promise<void>
}

const DB_NAME = 'kc-content'
const STORE = 'bundles'
const VERSION = 1

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' })
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
  })
}

/**
 * 内容缓存只存原始 bundle 与远端 sha，不存解析后的 Library：
 * Library 带函数与 Map，结构化克隆不可靠，而且解析很便宜。
 *
 * 隐私模式或禁用 IndexedDB 时退回内存缓存，当前会话仍可用，刷新即失效。
 */
export class IndexedDbCache implements ContentCache {
  readonly #memory = new Map<string, CachedBundle>()
  #db: Promise<IDBDatabase | null> | null = null

  #getDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') return Promise.resolve(null)
    if (this.#db === null) {
      this.#db = openDb().catch(() => null)
    }
    return this.#db
  }

  async get(key: string): Promise<CachedBundle | null> {
    const db = await this.#getDb()
    if (db === null) return this.#memory.get(key) ?? null

    try {
      const tx = db.transaction(STORE, 'readonly')
      const record = await requestResult(tx.objectStore(STORE).get(key))
      if (record === undefined) return this.#memory.get(key) ?? null
      return record as CachedBundle
    } catch {
      return this.#memory.get(key) ?? null
    }
  }

  async put(record: CachedBundle): Promise<void> {
    this.#memory.set(record.key, record)
    const db = await this.#getDb()
    if (db === null) return

    try {
      const tx = db.transaction(STORE, 'readwrite')
      await requestResult(tx.objectStore(STORE).put(record))
    } catch {
      // 写缓存失败不影响本次内容；下次仍可从网络拿。
    }
  }

  async delete(key: string): Promise<void> {
    this.#memory.delete(key)
    const db = await this.#getDb()
    if (db === null) return

    try {
      const tx = db.transaction(STORE, 'readwrite')
      await requestResult(tx.objectStore(STORE).delete(key))
    } catch {
      // 忽略：内存里已经删了，页面可以继续。
    }
  }

  async clear(): Promise<void> {
    this.#memory.clear()
    const db = await this.#getDb()
    if (db === null) return

    try {
      const tx = db.transaction(STORE, 'readwrite')
      await requestResult(tx.objectStore(STORE).clear())
    } catch {
      // 忽略：已经清空内存缓存。
    }
  }
}
